import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';

export const WORKED_EXAMPLE = '7E8 07 59 02 FF 03 01 00 AF';

const FLOW = [
  ['Monitor runs', 'The ECU runs a diagnostic test (e.g. misfire counting) when its enable conditions are met.'],
  ['Fault detected', 'The test fails: testFailed (bit 0) and testFailedThisOperationCycle (bit 1) are set.'],
  ['Pending', 'pendingDTC (bit 2) — failed in this or the last cycle, not yet confirmed.'],
  ['Confirmed', 'confirmedDTC (bit 3) after the OEM-defined number of failing cycles; stored in non-volatile memory.'],
  ['MIL', 'warningIndicatorRequested (bit 7) — the check-engine / warning lamp is requested.'],
  ['Stored in memory', 'Code, status and optional snapshot / extended data survive key-off.'],
  ['Read', 'A tester reads it with UDS 0x19 (e.g. 19 02 FF) or OBD-II Mode 03 (stored) / 07 (pending) / 0A (permanent).'],
  ['Cleared', 'UDS 0x14 FF FF FF or OBD-II Mode 04 erases it; status restarts at 0x50 until the monitor runs again.'],
];

const STEPS = [
  ['7E8', 'CAN ID: response from OBD ECU #1 (usually the engine ECU; it was asked on 7E0 or 7DF).'],
  ['07', 'ISO-TP single frame: 7 data bytes follow.'],
  ['59', 'Positive response to service 0x19 ReadDTCInformation (0x19 + 0x40).'],
  ['02', 'Sub-function reportDTCByStatusMask.'],
  ['FF', 'DTCStatusAvailabilityMask: this ECU supports all 8 status bits.'],
  ['03 01 00', 'DTC: 03 01 → P0301 (bits 00 = P, 00 = digit 0, then 3, 0, 1); FTB 00 = no sub-type.'],
  ['AF', 'Status 1010 1111: bits 0, 1, 2, 3, 5 and 7 are set.'],
];

// Educational section: DTC lifecycle flow, and one worked example from raw CAN bytes
// to a plain-English conclusion.
const HowDtcsWork = ({ onTryExample }) => (
  <Card className="technical-card" data-testid="dtc-how-it-works">
    <CardHeader>
      <CardTitle className="font-chivo uppercase tracking-tight text-base">How DTCs Work</CardTitle>
      <CardDescription>From a failing monitor to a code on the tester — and one worked example from raw bytes to a conclusion.</CardDescription>
    </CardHeader>
    <CardContent className="space-y-5 text-sm leading-relaxed text-foreground/90">
      <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {FLOW.map(([title, text], i) => (
          <li key={title} className="rounded-md border border-structure p-3 space-y-1">
            <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{i + 1}. {title}</p>
            <p className="text-xs">{text}</p>
          </li>
        ))}
      </ol>

      <div className="space-y-2">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Worked example</p>
        <p className="font-mono text-base font-bold break-all">{WORKED_EXAMPLE}</p>
        <ul className="space-y-1.5 text-xs">
          {STEPS.map(([bytes, text]) => (
            <li key={bytes} className="flex gap-3"><span className="font-mono font-semibold w-16 shrink-0">{bytes}</span><span>{text}</span></li>
          ))}
        </ul>
        <div className="rounded-md bg-muted/40 px-4 py-3 text-sm">
          <span className="font-semibold">Conclusion: </span>
          the engine ECU reports a cylinder 1 misfire (P0301) that is failing right now (bit 0), has failed this drive (bit 1), is pending (bit 2) and is confirmed (bit 3), and the ECU is asking for the check-engine lamp (bit 7). This is a live, confirmed fault rather than history, so start with the spark plug, coil and injector on cylinder 1.
        </div>
        {onTryExample && (
          <Button variant="outline" size="sm" onClick={() => onTryExample(WORKED_EXAMPLE)} data-testid="dtc-how-try-example">Decode this example</Button>
        )}
      </div>
    </CardContent>
  </Card>
);

export default HowDtcsWork;
